import {Fragment, useCallback, useMemo, useState} from "react";
import {useAppDispatch, useAppSelector} from "config/store";
import dayjs from "dayjs";
import {searchActions} from "config/store/modules/search.ts";
import {
    offset,
    useFloating,
    useHover,
    useInteractions
} from "@floating-ui/react";
import {useTranslation} from "react-i18next";

const now = dayjs();

function DaysNavBar() {
    const selectedDay = useAppSelector(state => state.search.selectedDay)
    const dispatch = useAppDispatch()
    const currentLang = useAppSelector(state => state.global.currentLang)

    const days = useMemo<{ week: string, day: string }[]>(() => {
        //start with now, then the next 6 days
        return ([0, 1, 2, 3, 4, 5, 6] as const).map((number) => {
            const day = dayjs().add(number, 'days')
            return {
                week: day.format('dddd')[0],
                day: day.format('YYYY-MM-DD')
            }
        })
        //dayjs need to reload when lang change
        // eslint-disable-next-line
    }, [currentLang])

    const onClick = useCallback((day: string) => {
        dispatch(searchActions.setSelectedDay(day))
    }, [dispatch])


    return (
        <div className="absolute left-1/2 top-3 sm:top-6 z-10 -translate-x-1/2">
            <div className="w-full overflow-hidden rounded-lg bg-white p-1 shadow-lg">
                <div className="flex w-full flex-row items-center justify-center ">
                    {days.map(day => (
                        <DayItem key={day.day} day={day} onClick={onClick} isSelected={selectedDay === day.day}/>
                    ))}
                </div>
            </div>
        </div>
    )
}

function DayItem(props: { day: { week: string, day: string }, onClick: (day: string) => void, isSelected: boolean }) {
    const {day, onClick, isSelected} = props
    const {t} = useTranslation()

    const [isOpen, setIsOpen] = useState(false);

    const dayStr = useMemo(() => {
        const dateDayJs = dayjs(day.day, 'YYYY-MM-DD');
        if (dateDayJs.isSame(now, 'day')) {
            return t('today')
        }
        return dateDayJs.format('LL')
    }, [t, day])

    const {refs, floatingStyles, context} = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [offset(10)],
        placement: 'bottom'
        //whileElementsMounted: autoUpdate,
    });

    const hover = useHover(context);

    // Merge all the interactions into prop getters
    const {getReferenceProps, getFloatingProps} = useInteractions([
        hover
    ]);

    return (
        <Fragment>
            <button
                ref={refs.setReference}
                {...getReferenceProps()}
                onClick={() => onClick(day.day)}
                type="button"
                className={`h-[48px] w-[48px] min-w-[48px] cursor-pointer appearance-none rounded-full text-center font-medium uppercase ${isSelected ? 'bg-green-150 text-green-500' : ''}`}>
                {day.week}
            </button>

            {isOpen && (
                <div
                    ref={refs.setFloating}
                    style={{
                        ...floatingStyles,
                        zIndex: isOpen ? 4 : 3
                    }}
                    {...getFloatingProps()}
                >
                    <div className="rounded-full shadow bg-white px-2 py-1">
                        <p className="text-xs font-medium capitalize text-black">{dayStr}</p>
                    </div>

                </div>
            )}

        </Fragment>
    )
}


export default DaysNavBar
